import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { ApiError } from '@/api/client';
import { submitInquiry } from '@/api/inquiries';

interface EditRequestFormProps {
  studioId: number;
  studioTitle: string;
  onDirtyChange: (isDirty: boolean) => void;
  onCancel: () => void;
  aiIdPrefix?: string;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export function EditRequestForm({
  studioId,
  studioTitle,
  onDirtyChange,
  onCancel,
  aiIdPrefix = 'edit-request-form',
}: EditRequestFormProps) {
  const [content, setContent] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [message, setMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isDirty = useMemo(() => {
    return content.trim().length > 0 || imageFiles.length > 0;
  }, [content, imageFiles]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    return () => onDirtyChange(false);
  }, [onDirtyChange]);

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    setImageFiles(Array.from(event.target.files ?? []));
    setStatus('idle');
    setMessage('');
  }

  function handleContentChange(event: ChangeEvent<HTMLTextAreaElement>) {
    setContent(event.target.value);
    setStatus('idle');
    setMessage('');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedContent = content.trim();
    if (!trimmedContent) {
      setStatus('error');
      setMessage('수정 내용을 입력해주세요.');
      return;
    }

    setStatus('submitting');
    setMessage('');

    try {
      const response = await submitInquiry({
        studioId,
        content: trimmedContent,
        imageFiles,
      });

      setContent('');
      setImageFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onDirtyChange(false);
      setStatus('success');
      setMessage(response.message || '수정요청이 접수되었습니다.');
    } catch (error) {
      setStatus('error');
      setMessage(getSubmitErrorMessage(error));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-ai-id={aiIdPrefix}>
      <div data-ai-id={`${aiIdPrefix}-fields-wrapper`}>
        <div data-ai-id={`${aiIdPrefix}-content-field`}>
          <label
            htmlFor={`edit-request-content-${studioId}`}
            className="block text-sm font-semibold text-slate-800"
            data-ai-id={`${aiIdPrefix}-content-field-label`}
          >
            <span data-ai-id={`${aiIdPrefix}-content-field-label-text`}>수정 내용</span>
          </label>
          <div className="mt-2" data-ai-id={`${aiIdPrefix}-content-field-input-wrapper`}>
            <textarea
              id={`edit-request-content-${studioId}`}
              value={content}
              onChange={handleContentChange}
              rows={5}
              required
              placeholder={`${studioTitle}의 위치, 지도 링크, 가는길 안내 등 수정할 내용을 적어주세요.`}
              className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base leading-6 text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              data-ai-id={`${aiIdPrefix}-content-field-textarea`}
            />
          </div>
        </div>
      </div>

      <div data-ai-id={`${aiIdPrefix}-images-field`}>
        <label
          htmlFor={`edit-request-images-${studioId}`}
          className="block text-sm font-semibold text-slate-800"
          data-ai-id={`${aiIdPrefix}-images-field-label`}
        >
          <span data-ai-id={`${aiIdPrefix}-images-field-label-text`}>사진 첨부</span>{' '}
          <span className="font-normal text-slate-400" data-ai-id={`${aiIdPrefix}-images-field-label-optional-text`}>
            선택
          </span>
        </label>
        <div className="mt-2" data-ai-id={`${aiIdPrefix}-images-field-input-wrapper`}>
          <input
            ref={fileInputRef}
            id={`edit-request-images-${studioId}`}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
            multiple
            onChange={handleFileChange}
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-xl file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-slate-700"
            data-ai-id={`${aiIdPrefix}-images-field-input`}
          />
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-500" data-ai-id={`${aiIdPrefix}-images-field-helper`}>
          <span data-ai-id={`${aiIdPrefix}-images-field-helper-text`}>
            찾기 어려운 입구, 건물 사진, 안내 이미지 등을 첨부할 수 있어요. 이미지는 8MB 이하로 올려주세요.
          </span>
        </p>
        {imageFiles.length > 0 ? (
          <p className="mt-2 text-xs font-medium text-slate-600" data-ai-id={`${aiIdPrefix}-images-field-selected-count`}>
            <span data-ai-id={`${aiIdPrefix}-images-field-selected-count-label-text`}>선택한 사진</span>{' '}
            <span data-ai-id={`${aiIdPrefix}-images-field-selected-count-value-text`}>{imageFiles.length}</span>
            <span data-ai-id={`${aiIdPrefix}-images-field-selected-count-unit-text`}>개</span>
          </p>
        ) : null}
      </div>

      {message ? (
        <p
          className={`rounded-2xl px-4 py-3 text-sm leading-6 ${
            status === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
          }`}
          role={status === 'error' ? 'alert' : 'status'}
          data-ai-id={status === 'success' ? `${aiIdPrefix}-submit-success-message` : `${aiIdPrefix}-submit-error-message`}
        >
          <span data-ai-id={`${aiIdPrefix}-submit-message-text`}>{message}</span>
        </p>
      ) : null}

      <div className="grid grid-cols-2 gap-2" data-ai-id={`${aiIdPrefix}-actions`}>
        <button
          type="button"
          onClick={onCancel}
          disabled={status === 'submitting'}
          className="h-11 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-100"
          data-ai-id={`${aiIdPrefix}-actions-cancel-button`}
        >
          <span data-ai-id={`${aiIdPrefix}-actions-cancel-button-label-text`}>취소</span>
        </button>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="h-11 rounded-2xl bg-emerald-600 px-4 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-70 focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-100"
          data-ai-id={`${aiIdPrefix}-actions-submit-button`}
        >
          <span data-ai-id={`${aiIdPrefix}-actions-submit-button-label-text`}>
            {status === 'submitting' ? '보내는 중' : '수정요청 보내기'}
          </span>
        </button>
      </div>
    </form>
  );
}

function getSubmitErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.message) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return '수정요청을 보내지 못했어요. 잠시 후 다시 시도해주세요.';
}
